import React, { useCallback, useMemo } from "react";
import { useLocation, navigate } from "@reach/router";
import { SettingsPageLayout } from "newui/features/Settings/components/SettingsPageLayout";
import type { TabMenuItem } from "newui/components/TabComponent/TabComponent";
import type { Company } from "newui/features/Company/types";
import { ScoringInstructions } from "./components/ScoringInstructions/ScoringInstructions";
import { EvaluationPermissions } from "./components/EvaluationPermissions/EvaluationPermissions";
import { useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { QuoteTemplateSettings } from "newui/features/Rfx/components/QuoteTable/QuoteTableSettings";
import { CompanyScheduleSettings } from "../Rfx/components/ScheduleOfRates";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

interface RfxSettingsProps {
  company: Company;
  /** Path prop is consumed by @reach/router; not used directly. */
  path?: string;
}

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const SCORING_INSTRUCTIONS = "Scoring Instructions";
const EVALUATION_PERMISSIONS = "Evaluation Permissions";
const SCHEDULE_OF_RATES = "Schedule of Rates";
const QUOTE_TEMPLATES = "Quote Templates";

const PROCURER_TABS: TabMenuItem[] = [
  { id: "scoring-instructions", name: SCORING_INSTRUCTIONS, showBadge: false, total: 0 },
  { id: "evaluation-permissions", name: EVALUATION_PERMISSIONS, showBadge: false, total: 0 },
  { id: "quote-templates", name: QUOTE_TEMPLATES, showBadge: false, total: 0 },
];

const SUPPLIER_TABS: TabMenuItem[] = [
  { id: "schedule-of-rates", name: SCHEDULE_OF_RATES, showBadge: false, total: 0 },
];

const SLUG_TO_NAME: Record<string, string> = {
  "scoring-instructions": SCORING_INSTRUCTIONS,
  "evaluation-permissions": EVALUATION_PERMISSIONS,
  "schedule-of-rates": SCHEDULE_OF_RATES,
  "quote-templates": QUOTE_TEMPLATES,
};

const NAME_TO_SLUG: Record<string, string> = Object.entries(SLUG_TO_NAME).reduce(
  (acc, [slug, name]) => ({ ...acc, [name]: slug }),
  {}
);

/* -------------------------------------------------------------------------- */
/* URL parsing                                                                */
/* -------------------------------------------------------------------------- */

interface ParsedRoute {
  basePath: string; // everything up to and including /rfx-settings
  tabSlug: string | null;
  editingId: number | null;
  isNew: boolean;
}

const parseRoute = (pathname: string): ParsedRoute => {
  const marker = "/rfx-settings";
  const idx = pathname.indexOf(marker);
  if (idx === -1) {
    return { basePath: pathname.replace(/\/$/, ""), tabSlug: null, editingId: null, isNew: false };
  }
  const basePath = pathname.slice(0, idx + marker.length);
  const remainder = pathname.slice(idx + marker.length).replace(/^\/+|\/+$/g, "");
  if (!remainder) {
    return { basePath, tabSlug: null, editingId: null, isNew: false };
  }
  const [slug, second] = remainder.split("/");
  if (!second) {
    return { basePath, tabSlug: slug, editingId: null, isNew: false };
  }
  if (second === "new") {
    return { basePath, tabSlug: slug, editingId: null, isNew: true };
  }
  const parsed = Number(second);
  return {
    basePath,
    tabSlug: slug,
    editingId: Number.isFinite(parsed) ? parsed : null,
    isNew: false,
  };
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export const RfxSettings: React.FC<RfxSettingsProps> = ({ company }) => {
  const { isClientProfileActive } = useProfileContext();
  const location = useLocation();

  const tabs = useMemo(
    () => (isClientProfileActive ? PROCURER_TABS : SUPPLIER_TABS),
    [isClientProfileActive]
  );

  const defaultTab = tabs[0]?.name ?? SCORING_INSTRUCTIONS;
  const defaultSlug = tabs[0]?.id ?? "scoring-instructions";

  const parsed = useMemo(() => parseRoute(location.pathname), [location.pathname]);

  // Resolve the active tab from the URL, falling back to the first available tab.
  const resolvedTabName = useMemo(() => {
    if (parsed.tabSlug && SLUG_TO_NAME[parsed.tabSlug]) {
      const name = SLUG_TO_NAME[parsed.tabSlug];
      // Only honour the slug if it belongs to the current profile's tabs.
      if (tabs.some((t) => t.name === name)) {
        return name;
      }
    }
    return defaultTab;
  }, [parsed.tabSlug, tabs, defaultTab]);

  const changeActiveTab = useCallback(
    (name: string): void => {
      const slug = NAME_TO_SLUG[name] ?? defaultSlug;
      navigate(`${parsed.basePath}/${slug}`);
    },
    [parsed.basePath, defaultSlug]
  );

  const handleQuoteTemplateEditingChange = useCallback(
    (id: number | null, opts?: { isNew?: boolean }) => {
      const base = `${parsed.basePath}/quote-templates`;
      if (opts?.isNew) {
        navigate(`${base}/new`);
        return;
      }
      navigate(id == null ? base : `${base}/${id}`);
    },
    [parsed.basePath]
  );

  return (
    <SettingsPageLayout
      heading="RFx Settings"
      tabs={tabs}
      activeTab={resolvedTabName}
      onTabChange={changeActiveTab}
    >
      {resolvedTabName === SCORING_INSTRUCTIONS && (
        <ScoringInstructions company={company} />
      )}
      {resolvedTabName === EVALUATION_PERMISSIONS && (
        <EvaluationPermissions company={company} />
      )}
      {resolvedTabName === SCHEDULE_OF_RATES && company?.id && (
        <CompanyScheduleSettings companyId={company.id} />
      )}
      {resolvedTabName === QUOTE_TEMPLATES && company?.id && (
        <QuoteTemplateSettings
          companyId={company.id}
          editingId={parsed.editingId}
          isCreatingNew={parsed.isNew}
          onEditingChange={handleQuoteTemplateEditingChange}
        />
      )}
    </SettingsPageLayout>
  );
};
